
import { useState, useEffect } from 'react'
import { useBusinessTrips } from '../hooks/use-entity'
import { useApiClient } from '../lib/api-client'

import { getStatusLabel, getStatusClass } from '../lib/workflow-helpers'

import { Pagination } from './Pagination'
import { SearchBox } from './SearchBox'

const PAGE_SIZE = 20

interface BusinessTrip {

  id: string

  tripDate: Date

  destination: string

  purpose: string

  amount: number

  remarks?: string

  status: string

  requesterId: string

  requesterUsername?: string

  lastActionActorId?: string

  lastActionActorUsername?: string

  lastActionLabel?: string

  lastActionComment?: string

  createdAt?: Date

  updatedAt?: Date

}
const TEMPLATE_LABELS: Record<string, string> = {
  'business_trip': '出張申請',
};

type StatusTab = 'all' | 'in_progress' | 'completed' | 'rejected' | 'draft'

interface RequestListProps {
  viewMode?: 'my_requests' | 'pending_approval'
  onViewRequest: (id: string) => void
  onEditRequest: (id: string) => void
}

function RequestList({ viewMode = 'my_requests', onViewRequest, onEditRequest }: RequestListProps) {
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [actionError, setActionError] = useState<string | null>(null)
  const [statusTab, setStatusTab] = useState<StatusTab>('all')
  const [q, setQ] = useState('')
  const [draftDestination, setDraftDestination] = useState('')
  const [appliedDestination, setAppliedDestination] = useState('')
  const [draftPurpose, setDraftPurpose] = useState('')
  const [appliedPurpose, setAppliedPurpose] = useState('')
  const [draftAmountMin, setDraftAmountMin] = useState('')
  const [draftAmountMax, setDraftAmountMax] = useState('')
  const [appliedAmountMin, setAppliedAmountMin] = useState('')
  const [appliedAmountMax, setAppliedAmountMax] = useState('')
  const [draftTripDateFrom, setDraftTripDateFrom] = useState('')
  const [draftTripDateTo, setDraftTripDateTo] = useState('')
  const [appliedTripDateFrom, setAppliedTripDateFrom] = useState('')
  const [appliedTripDateTo, setAppliedTripDateTo] = useState('')
  const [draftCreatedAtFrom, setDraftCreatedAtFrom] = useState('')
  const [draftCreatedAtTo, setDraftCreatedAtTo] = useState('')
  const [appliedCreatedAtFrom, setAppliedCreatedAtFrom] = useState('')
  const [appliedCreatedAtTo, setAppliedCreatedAtTo] = useState('')
  const [draftStatusValues, setDraftStatusValues] = useState<string[]>([])
  const [appliedStatusValues, setAppliedStatusValues] = useState<string[]>([])
  const api = useApiClient()


  // all possible workflow status options (including metadata for tab filtering)
  const ALL_STATUS_OPTIONS: Array<{ value: string; label: string; initial?: boolean; terminal?: boolean; styleType?: string }> = [
    { value: 'draft', label: '下書き', initial: true, styleType: 'neutral' },
    { value: 'pending_supervisor', label: '上司承認待ち', styleType: 'warning' },
    { value: 'approved', label: '承認済', terminal: true, styleType: 'success' },
    { value: 'rejected', label: '差戻し', styleType: 'danger' },
    { value: 'deleted', label: '削除済', terminal: true, styleType: 'neutral' },
  ]

  const getStatusOptionsForTab = (tab: StatusTab) => {
    const filtered = ALL_STATUS_OPTIONS.filter((s) => {
      if (tab === 'all') return true
      if (tab === 'draft') return s.initial === true
      if (tab === 'completed') return s.terminal === true
      if (tab === 'rejected') return s.styleType === 'danger'
      if (tab === 'in_progress') return !(s.initial === true) && !(s.terminal === true) && s.styleType !== 'danger'
      return true
    })
    return filtered.map(({ value, label }) => ({ value, label }))
  }

  const statusOptions = getStatusOptionsForTab(statusTab)

  // use hook to fetch data
  const filters = {
    statusTab,
    limit: PAGE_SIZE,
    offset,
    q: q || undefined,
    destination: appliedDestination || undefined,
    purpose: appliedPurpose || undefined,
    amountMin: appliedAmountMin || undefined,
    amountMax: appliedAmountMax || undefined,
    tripDateFrom: appliedTripDateFrom || undefined,
    tripDateTo: appliedTripDateTo || undefined,
    createdAtFrom: appliedCreatedAtFrom || undefined,
    createdAtTo: appliedCreatedAtTo || undefined,
    status: appliedStatusValues.length ? appliedStatusValues.join(',') : undefined,
  }

  const { data: queryResult, isLoading, error: queryError, refetch } = useBusinessTrips(viewMode === 'my_requests' ? 'my_requests' : viewMode === 'pending_approval' ? 'pending_approval' : undefined, filters)

  useEffect(() => {
    if (isLoading) {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [isLoading])

  // 列フィルタの不正値（範囲逆転・型不一致等）はAPIが400を返すため、ユーザーに提示する
  const filterErrorMessage = queryError
    ? ((queryError as any)?.response?.data?.error?.message ?? '絞り込み条件が不正です。条件を見直してください。')
    : null

  useEffect(() => {
    if (viewMode === 'pending_approval' && statusTab === 'draft') {
      setStatusTab('all')
      return
    }
    // reset offset when immediate-apply条件（statusTab・キーワード）が変わったとき
    setOffset(0)
  }, [viewMode, statusTab, q])
  
  // statusTab変更時、列フィルタのstatus値がタブ対象外ならクリア
  useEffect(() => {
    const allowed = new Set(getStatusOptionsForTab(statusTab).map((s) => s.value))
    const filteredDraft = draftStatusValues.filter((v) => allowed.has(v))
    const filteredApplied = appliedStatusValues.filter((v) => allowed.has(v))
    if (filteredDraft.join(',') !== draftStatusValues.join(',')) setDraftStatusValues(filteredDraft)
    if (filteredApplied.join(',') !== appliedStatusValues.join(',')) setAppliedStatusValues(filteredApplied)
  }, [statusTab])

  const handleSearch = (keyword: string) => {
    setQ(keyword)
    setOffset(0)
  }

  const isFiltersDirty = draftDestination !== appliedDestination || draftPurpose !== appliedPurpose || draftAmountMin !== appliedAmountMin || draftAmountMax !== appliedAmountMax || draftTripDateFrom !== appliedTripDateFrom || draftTripDateTo !== appliedTripDateTo || draftCreatedAtFrom !== appliedCreatedAtFrom || draftCreatedAtTo !== appliedCreatedAtTo || draftStatusValues.join(',') !== appliedStatusValues.join(',')
  const isFiltersEmpty = draftDestination === '' && appliedDestination === '' && draftPurpose === '' && appliedPurpose === '' && draftAmountMin === '' && draftAmountMax === '' && appliedAmountMin === '' && appliedAmountMax === '' && draftTripDateFrom === '' && draftTripDateTo === '' && appliedTripDateFrom === '' && appliedTripDateTo === '' && draftCreatedAtFrom === '' && draftCreatedAtTo === '' && appliedCreatedAtFrom === '' && appliedCreatedAtTo === '' && draftStatusValues.length === 0 && appliedStatusValues.length === 0

  const handleApplyFilters = () => {
    setAppliedDestination(draftDestination)
    setAppliedPurpose(draftPurpose)
    setAppliedAmountMin(draftAmountMin)
    setAppliedAmountMax(draftAmountMax)
    setAppliedTripDateFrom(draftTripDateFrom)
    setAppliedTripDateTo(draftTripDateTo)
    setAppliedCreatedAtFrom(draftCreatedAtFrom)
    setAppliedCreatedAtTo(draftCreatedAtTo)
    setAppliedStatusValues(draftStatusValues)
    setOffset(0)
  }

  const handleClearFilters = () => {
    setDraftDestination('')
    setAppliedDestination('')
    setDraftPurpose('')
    setAppliedPurpose('')
    setDraftAmountMin('')
    setDraftAmountMax('')
    setAppliedAmountMin('')
    setAppliedAmountMax('')
    setDraftTripDateFrom('')
    setDraftTripDateTo('')
    setAppliedTripDateFrom('')
    setAppliedTripDateTo('')
    setDraftCreatedAtFrom('')
    setDraftCreatedAtTo('')
    setAppliedCreatedAtFrom('')
    setAppliedCreatedAtTo('')
    setDraftStatusValues([])
    setAppliedStatusValues([])
    setOffset(0)
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('この申請を削除してもよろしいですか？')) return
    setActionError(null)
    try {
      await api.delete(`/businesstrips/${id}`)
      // re-fetch via hook
      refetch()
    } catch (err: any) {
      setActionError(err?.response?.data?.error?.message ?? err?.message ?? '削除に失敗しました')
    }
  }

  const requests = queryResult?.data ?? []
  const total = queryResult?.total ?? 0

  if (loading) return <div>読み込み中...</div>

  return (
    <div className="request-list">
      <h2>{viewMode === 'pending_approval' ? '承認一覧' : '申請一覧'}</h2>
      <SearchBox defaultValue={q} onSearch={handleSearch} />
    {filterErrorMessage && <p className="error" data-testid="filter-error">{filterErrorMessage}</p>}
    {actionError && <p className="error" data-testid="action-error">{actionError}</p>}
      <div className="list-tabs">
        <button className={`tab-btn${statusTab === 'all' ? ' active' : ''}`} onClick={() => setStatusTab('all')}>
          すべて
        </button>
        <button className={`tab-btn${statusTab === 'in_progress' ? ' active' : ''}`} onClick={() => setStatusTab('in_progress')}>
          進行中
        </button>
        <button className={`tab-btn${statusTab === 'completed' ? ' active' : ''}`} onClick={() => setStatusTab('completed')}>
          完了
        </button>
        <button className={`tab-btn${statusTab === 'rejected' ? ' active' : ''}`} onClick={() => setStatusTab('rejected')}>
          差戻し
        </button>
        {viewMode === 'my_requests' && (
          <button className={`tab-btn${statusTab === 'draft' ? ' active' : ''}`} onClick={() => setStatusTab('draft')}>
            下書き
          </button>
        )}
      </div>
      <table className="data-table" data-testid="entity-table">
        <thead>
          <tr>
            <th>申請分類</th>




            <th>出張日</th>



            <th>行き先</th>



            <th>目的</th>



            <th>金額</th>





            <th>status</th>















            <th>createdAt</th>




            <th>操作</th>
          </tr>
          <tr className="filter-row" data-testid="column-filter-row">
            <th>-</th>





            <th>
              <div className="filter-date-range">
                <div className="filter-range-row">
                  <input type="date" value={draftTripDateFrom} onChange={(e) => setDraftTripDateFrom(e.target.value)} aria-label="出張日の開始日" data-testid="filter-tripDate-from" />
                  <span className="filter-range-label">から</span>
                </div>
                <div className="filter-range-row">
                  <input type="date" value={draftTripDateTo} onChange={(e) => setDraftTripDateTo(e.target.value)} aria-label="出張日の終了日" data-testid="filter-tripDate-to" />
                  <span className="filter-range-label">まで</span>
                </div>
              </div>
            </th>





            <th>
              <input type="text" value={draftDestination} onChange={(e) => setDraftDestination(e.target.value)} placeholder="部分一致" aria-label="行き先で絞り込み" data-testid="filter-destination" />
            </th>





            <th>
              <input type="text" value={draftPurpose} onChange={(e) => setDraftPurpose(e.target.value)} placeholder="部分一致" aria-label="目的で絞り込み" data-testid="filter-purpose" />
            </th>





            <th>
              <div className="filter-number-range">
                <div className="filter-range-row">
                  <input type="number" value={draftAmountMin} onChange={(e) => setDraftAmountMin(e.target.value)} placeholder="最小" aria-label="金額の最小値" data-testid="filter-amount-min" />
                  <span className="filter-range-label">以上</span>
                </div>
                <div className="filter-range-row">
                  <input type="number" value={draftAmountMax} onChange={(e) => setDraftAmountMax(e.target.value)} placeholder="最大" aria-label="金額の最大値" data-testid="filter-amount-max" />
                  <span className="filter-range-label">以下</span>
                </div>
              </div>
            </th>







            <th>
              <select multiple value={draftStatusValues} onChange={(e) => setDraftStatusValues(Array.from(e.target.selectedOptions).map((opt) => opt.value))} aria-label="statusで絞り込み（複数選択）" data-testid="filter-status">
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </th>

















            <th>
              <div className="filter-date-range">
                <div className="filter-range-row">
                  <input type="date" value={draftCreatedAtFrom} onChange={(e) => setDraftCreatedAtFrom(e.target.value)} aria-label="createdAtの開始日" data-testid="filter-createdAt-from" />
                  <span className="filter-range-label">から</span>
                </div>
                <div className="filter-range-row">
                  <input type="date" value={draftCreatedAtTo} onChange={(e) => setDraftCreatedAtTo(e.target.value)} aria-label="createdAtの終了日" data-testid="filter-createdAt-to" />
                  <span className="filter-range-label">まで</span>
                </div>
              </div>
            </th>





            <th>
              <div className="filter-actions-inline">
                <button type="button" className="btn-apply-filters" aria-label="列フィルタを適用" data-testid="apply-filters-button" disabled={!isFiltersDirty} onClick={handleApplyFilters}>適用</button>
                <button type="button" className="btn-clear-filters" aria-label="列フィルタをクリア" data-testid="clear-filters-button" disabled={isFiltersEmpty} onClick={handleClearFilters}>クリア</button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {requests.length === 0 ? (
            <tr>
              <td colSpan={ 7 + 1}>データがありません</td>
            </tr>
          ) : (
            requests.map((req: BusinessTrip) => (
              <tr key={req.id} data-testid={`entity-row-${req.id}`}>
                <td>{TEMPLATE_LABELS[(req as any).templateName] || '-'}</td>





                <td>{req.tripDate ? new Date(req.tripDate as Date).toLocaleDateString() : '-'}</td>





                <td>{req.destination}</td>





                <td>{req.purpose}</td>





                <td>{req.amount}</td>







                <td>
                  <span className={getStatusClass(req.status as string)}>
                    {getStatusLabel(req.status as string)}
                  </span>
                </td>

















                <td>{req.createdAt ? new Date(req.createdAt as Date).toLocaleDateString() : '-'}</td>





                <td className="action-buttons">
                  <button data-testid={`action-view-${req.id}`} onClick={() => onViewRequest(req.id)}>詳細</button>
                  <button data-testid={`action-edit-${req.id}`} onClick={() => onEditRequest(req.id)} className="btn-edit">編集</button>
                  <button data-testid={`action-delete-${req.id}`} onClick={() => handleDelete(req.id)} className="btn-delete">削除</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={(o:number) => setOffset(o)} />
    </div>
  )
}

export default RequestList